import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Checkbox, Grid, Icon, Typography } from '@material-ui/core';
import { Form, LawLink } from '..';
import { useConfirmation } from '../../Context/ConfirmationContext/ConfirmationContext';
import { UserContext } from '../../Context/UserContext/UserContext';
import moment from 'moment';
import { TextField } from '../Field/TextField';
import { SelectField } from '../Field/SelectField';
import { SelectOption, TypedFormFieldValue } from '../Form/Form.types';
import { useAptorApi } from '../../Api';

export interface IChange {
  id: number;
  number: string;
  effectiveFrom: Date;
  shortDescription: string;
  reprint?: string;
  extent?: string;
  urlAddress: string;
  unacknowledgedByUsers?: number[];
  delegatedUsersIds?: number[];
  responsibleUsers?: { id: number; name: string }[];
  hasTransitionalRegulation: boolean;
  includeInSendouts: boolean;
}

interface IProps {
  lawId: number;
  change: IChange;
  acknowledge?: (comment?: string, affect?: any) => void;
  delegate?: (userIds: number[]) => void;
}

export const LawChange: FC<IProps> = ({ lawId, change, acknowledge, delegate }) => {
  const { complianceUserFor, id } = useContext(UserContext);
  const { deleteConfirmation } = useConfirmation();
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();

  const [comment, setComment] = useState<string>('');
  const [affect, setAffect] = useState(false)
  const [notaffect, setNotAffect] = useState(false)
  const [delegateUsers, setDelegateUsers] = useState<SelectOption[]>([]);
  const [customerUserOptions, setCustomerUserOptions] = useState<SelectOption[]>([]);
  const [delegateOptionsLoaded, setDelegateOptionsLoaded] = useState(false);
  const isDelegatedUser = change.delegatedUsersIds?.some((x) => x === id) ?? false;
  const shouldAcknowledge =
    acknowledge &&
    (complianceUserFor.laws.some((x) => x.id === lawId) || isDelegatedUser) &&
    change.unacknowledgedByUsers &&
    change.unacknowledgedByUsers.some((x) => x === id);
  const shouldDelegate = (change.unacknowledgedByUsers ?? [])
    .filter((userId) => !(change.delegatedUsersIds ?? []).includes(userId))
    .some((userId) => userId === id);


  const confirmation = {
    title: formatMessage({ id: 'component.law-change.acknowledge.label' }),
    description: formatMessage({ id: 'component.law-change.acknowledge.confirmation' }),
  };
  const delegateConfirmation = {
    title: formatMessage({ id: 'component.law-change.acknowledge.label' }),
    description: formatMessage({ id: 'component.law-change.delegate.confirmation' }),
  };

  const ack: any = () => {
    let affected
    if (affect == false && notaffect == false) {
      affected = null
    } else if (affect == true) {
      affected = true
    } else if (notaffect == true) {
      affected = false
    }
    return acknowledge!(comment, affected);
  };

  const handleAcknowledge = () => {
    if (acknowledge) {
      deleteConfirmation(ack, confirmation);
    }
  };

  useEffect(() => {
    if (!shouldAcknowledge) {
      return;
    }
    api
      .getCustomerUsers<{ id: number; name: string }>()
      .then((result) => {
        if (!abortController.current.signal.aborted) {
          const options = (result?.items || []).map((user) => ({ value: user.id, label: user.name }));
          const responsibleUserIds = (change.responsibleUsers ?? []).map((user) => user.id);
          const eligibleOptions = options.filter((option) => !responsibleUserIds.includes(Number(option.value)));
          setCustomerUserOptions(eligibleOptions);
          if (change.delegatedUsersIds?.length) {
            setDelegateUsers(options.filter((option) => change.delegatedUsersIds!.includes(Number(option.value))));
          }
          setDelegateOptionsLoaded(true);
        }
      })
      .catch((error) => {
        if (!abortController.current.signal.aborted) {
          console.error('Failed to load customer users:', error);
          setDelegateOptionsLoaded(true);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAcknowledge]);

  const handleDelegateChange = useCallback(
    (val: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
      if (Array.isArray(val)) {
        setDelegateUsers(val as SelectOption[]);
      } else if (val) {
        setDelegateUsers([val as SelectOption]);
      } else {
        setDelegateUsers([]);
      }
    },
    [],
  );

  const delegateAction = async () => {
    const userIds = delegateUsers.map((user) => Number(user.value));
    await api.updateLawChangeDelegatedUsers(lawId, change.id, userIds);
    if (abortController.current.signal.aborted) {
      return;
    }
    if (delegate) {
      delegate(userIds);
    }
  };

  const handleDelegate = () => {
    deleteConfirmation(delegateAction, delegateConfirmation);
  };


  return (
    <Grid container direction="column">
      <Typography dangerouslySetInnerHTML={{ __html: change.shortDescription }} />
      {change.extent && <Typography>{change.extent}</Typography>}
      <LawLink type="law-change" url={change.urlAddress} />
      <Typography>
        <FormattedMessage
          id="component.law-change.effectiveFrom"
          values={{ date: moment(change.effectiveFrom).format('ll') }}
        />
      </Typography>
      {change.hasTransitionalRegulation === true && (
        <Typography>
          <FormattedMessage id="component.law-change.has-transitional-regulation" />
        </Typography>
      )}
      {change.reprint && (
        <Typography>
          <FormattedMessage id="component.law-change.reprint" values={{ reprint: change.reprint }} />
        </Typography>
      )}

      {(change.includeInSendouts && (
        <Typography>
          <FormattedMessage id="component.law-change.included-in-sendouts" />
        </Typography>
      )) || (
          <Typography>
            <FormattedMessage id="component.law-change.not-included-in-sendouts" />
          </Typography>
        )}
      {shouldAcknowledge && (
        <>
          <Grid md={12} xs={12} item style={{ marginTop: '2em', marginBottom: '1em', display: 'flex' }}>
            <div>
              <Checkbox
                checked={affect}
                // color="primary"
                name='affect'
                onChange={(_, value: any) => { 
                  if(notaffect==true){
                    setAffect(value)
                    setNotAffect(false)
                  }else{
                    setAffect(value)
                  }
                }}
                inputProps={{ 'aria-label': 'primary checkbox' }}
                // onFocus={() => setIsFocused(true)}
                // onBlur={() => setIsFocused(false)}
                required={false}

              />
              <label>{formatMessage({ id: "law-portal.our-law.ack.latest-change.checkbox.text.affect" })}</label>
            </div>
            <div>
              <Checkbox
                checked={notaffect}
                // color="primary"
                name='not_affect'
                onChange={(_, value: any) => { 
                  if(affect==true){
                    setNotAffect(value)
                    setAffect(false)
                  }else{
                    setNotAffect(value)
                  }
                }}
                inputProps={{ 'aria-label': 'primary checkbox' }}
                // onFocus={() => setIsFocused(true)}
                // onBlur={() => setIsFocused(false)}
                required={false}

              />
              <label>{formatMessage({ id: "law-portal.our-law.ack.latest-change.checkbox.text.not_affect" })}</label>
            </div>
          </Grid>
          <Grid md={6} xs={12} item style={{ marginTop: '2em', marginBottom: '1em' }}>

            <TextField
              label={formatMessage({ id: 'component.law-change.acknowledge.comment' })}
              name="comment"
              errorTexts={[]}
              onChange={(val) => setComment(val?.value || '')}
            />
          </Grid>
          <Grid item container xs={12} alignItems="flex-end" spacing={2} style={{ marginBottom: '1em' }}>
            <Grid md={6} xs={12} item>
              {shouldDelegate && delegateOptionsLoaded && (
                <SelectField
                  label={formatMessage({ id: 'component.law-change.delegate.label' })}
                  name="delegateUsers"
                  options={customerUserOptions}
                  isMulti={true}
                  errorTexts={[]}
                  initialState={delegateUsers}
                  onChange={handleDelegateChange}
                />
              )}
            </Grid>
            <Grid md={6} xs={12} item>
              <Grid container justify="flex-end" spacing={2}>
                {shouldDelegate && (
                  <Grid item>
                    <Button variant="outlined" onClick={handleDelegate} startIcon={<Icon>check</Icon>}>
                      <FormattedMessage id="component.law-change.delegate.button" defaultMessage="Delegate" />
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <Button variant="outlined" onClick={handleAcknowledge} startIcon={<Icon>check</Icon>}>
                    <FormattedMessage id="component.law-change.acknowledge.button" defaultMessage="Acknowledge" />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};
